import { apiService } from '@shared/services/apiServices';
import { AxiosError } from 'axios';
import technicianEndpoints from './technicianEndpoints';
import { TECHNICIAN_ERROR_MESSAGES } from '../errors';

export type ViewTechnicianStatus = 'active' | 'inactive';

export interface ViewTechnicianProfile {
  path: string;
  fileSize: number;
  filename: string;
  isUploaded: boolean;
  resourceId: string;
  url?: string;
}

export interface ViewTechnicianDoc {
  path?: string;
  fileSize?: number;
  filename: string;
  isUploaded?: boolean;
  resourceId?: string;
  url?: string;
}

export interface ViewTechnician {
  id: string;
  technician_id: string;
  name: string;
  email: string;
  phone_number: string;
  country_code: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  role: string;
  status: ViewTechnicianStatus;
  profile: ViewTechnicianProfile | null;
  docs: ViewTechnicianDoc[];
  last_logged_in: string | null;
  created_at: string;
  updated_at: string;
}

export interface ViewTechnicianApiResponse {
  technician: ViewTechnician;
}

export interface ViewTechnicianResult {
  success: boolean;
  data?: ViewTechnicianApiResponse;
  error?: string;
}

export interface ViewTechnicianOptions {
  technicianId: string;
}

const ERROR_MESSAGES = {
  DEFAULT_ERROR: TECHNICIAN_ERROR_MESSAGES.DEFAULT_ERROR,
  NETWORK_ERROR: TECHNICIAN_ERROR_MESSAGES.NETWORK_ERROR,
  UNEXPECTED_ERROR: TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR,
} as const;

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const errorMessage = apiError.message || apiError.error;
    if (errorMessage) {
      return errorMessage;
    }
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }
  return ERROR_MESSAGES.DEFAULT_ERROR;
};

export const viewTechnician = async (
  options: ViewTechnicianOptions
): Promise<ViewTechnicianResult> => {
  const { technicianId } = options;

  if (!technicianId?.trim()) {
    return {
      success: false,
      error: ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const result = await apiService.getAPI(
      `${technicianEndpoints.TECHNICIANS}/${technicianId.trim()}`
    );

    if (!result.error) {
      return {
        success: true,
        data: result.data as ViewTechnicianApiResponse,
      };
    }

    const errorData = result.data;
    const isNetworkError = result.isNetworkError;
    const errorMessage = extractErrorMessage(errorData);

    if (isNetworkError) {
      return {
        success: false,
        error: ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: errorMessage,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      return {
        success: false,
        error: extractErrorMessage(errorData),
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
